import * as RadixSlider from '@radix-ui/react-slider';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { colors } from '../theme/colors.stylex';
import type { SliderProps } from './Slider.types';

const styles = stylex.create({
  root: {
    position: 'relative',
    display: 'flex',
    height: '1.25rem',
    width: '100%',
    touchAction: 'none',
    alignItems: 'center',
    userSelect: 'none',
  },
  track: {
    position: 'relative',
    height: '0.3125rem',
    flexGrow: 1,
    borderRadius: '0.25rem',
    backgroundColor: colors.trackBackground,
  },
  range: { position: 'absolute', height: '100%', borderRadius: '9999px' },
  thumb: {
    display: 'block',
    height: '0.9375rem',
    width: '0.9375rem',
    borderRadius: '9999px',
    backgroundColor: { 'default': colors.accent, ':active': colors.accentPressed },
    transitionProperty:
      'color, background-color, border-color, outline-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, translate, scale, rotate, filter, backdrop-filter',
    transitionDuration: '150ms',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    outlineStyle: 'none',
  },
  disabled: { backgroundColor: { 'default': colors.trackBackground, ':active': colors.trackBackground } },
});

/**
 * Slider component for selecting a value within a range.
 */
export const Slider = ({
  onValueChange,
  onPointerUp,
  id,
  value,
  name,
  disabled = false,
  rootExtraClassNames = '',
  trackExtraClassNames = '',
  rangeExtraClassNames = '',
  thumbExtraClassNames = '',
  xstyle,
  trackXstyle,
  rangeXstyle,
  thumbXstyle,
  step,
  min = 0,
  max = 100,
  maxAccessible,
}: SliderProps) => {
  const resolvedMaxAccessible = Math.min(Math.max(maxAccessible ?? max, min), max);
  const resolvedValue = Math.min(Math.max(value, min), resolvedMaxAccessible);

  const handleValueChange = (nextValue: number[]) => {
    if (!onValueChange) return;
    const clampedValue = Math.min(Math.max(nextValue[0] ?? min, min), resolvedMaxAccessible);
    onValueChange([clampedValue]);
  };

  const rootProps = stylex.props(styles.root, xstyle);
  const trackProps = stylex.props(styles.track, trackXstyle);
  const rangeProps = stylex.props(styles.range, rangeXstyle);
  const thumbProps = stylex.props(styles.thumb, disabled && styles.disabled, thumbXstyle);
  return (
    <RadixSlider.Root
      {...rootProps}
      className={clsx(rootProps.className, rootExtraClassNames)}
      defaultValue={[resolvedValue]}
      value={[resolvedValue]}
      onValueChange={handleValueChange}
      onPointerUp={(e) => onPointerUp && onPointerUp(e)}
      name={name}
      id={id}
      max={max}
      min={min}
      disabled={disabled}
      step={step}>
      <RadixSlider.Track {...trackProps} className={clsx(trackProps.className, trackExtraClassNames)}>
        <RadixSlider.Range {...rangeProps} className={clsx(rangeProps.className, rangeExtraClassNames)} />
      </RadixSlider.Track>
      <RadixSlider.Thumb
        {...thumbProps}
        className={clsx(thumbProps.className, thumbExtraClassNames)}
        aria-disabled={disabled}
      />
    </RadixSlider.Root>
  );
};
